'use client';

import { useEffect, useCallback, useRef } from 'react';
import { usePathname } from 'next/navigation';

interface UseInterceptNavigationProps {
  shouldIntercept: boolean;
  onInterceptAttempt: () => Promise<boolean>;
}

export const useInterceptNavigation = ({
  shouldIntercept,
  onInterceptAttempt,
}: UseInterceptNavigationProps) => {
  const pathname = usePathname();
  const isNavigatingRef = useRef(false);
  const isProcessingRef = useRef(false);
  const hasInitializedHistoryRef = useRef(false);

  const handleBeforeUnload = useCallback(
    (e: BeforeUnloadEvent) => {
      if (shouldIntercept && !isNavigatingRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    },
    [shouldIntercept],
  );

  const handlePopState = useCallback(
    async () => {
      if (shouldIntercept && !isNavigatingRef.current && !isProcessingRef.current) {
        isProcessingRef.current = true;

        window.history.pushState(null, document.title, window.location.href);

        try {
          const userConfirmed = await onInterceptAttempt();

          if (userConfirmed) {
            isNavigatingRef.current = true;
            isProcessingRef.current = false;
            window.history.go(-2);
          } else {
            isProcessingRef.current = false;
          }
        } catch {
          isProcessingRef.current = false;
        }
      }
    },
    [shouldIntercept, onInterceptAttempt],
  );

  const handleLinkClick = useCallback(
    async (e: MouseEvent) => {
      if (!shouldIntercept || isNavigatingRef.current || isProcessingRef.current) {
        return;
      }

      const target = e.target as HTMLElement;
      const link = target.closest('a[href]') as HTMLAnchorElement;

      if (!link) {
        return;
      }

      const href = link.getAttribute('href');
      const isExternal = link.target === '_blank' || link.getAttribute('rel')?.includes('external');
      const isDownload = link.hasAttribute('download');
      const isMailto = href?.startsWith('mailto:');
      const isTel = href?.startsWith('tel:');
      const isHash = href?.startsWith('#');
      const isSamePage = href === pathname || href?.startsWith(`${pathname}#`) || href?.startsWith(`${pathname}?`);

      if (isExternal || isDownload || isMailto || isTel || isHash || isSamePage || !href) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      isProcessingRef.current = true;

      try {
        const userConfirmed = await onInterceptAttempt();

        if (userConfirmed) {
          isNavigatingRef.current = true;
          isProcessingRef.current = false;
          link.click();
        } else {
          isProcessingRef.current = false;
        }
      } catch {
        isProcessingRef.current = false;
      }
    },
    [shouldIntercept, onInterceptAttempt, pathname],
  );

  useEffect(() => {
    if (shouldIntercept && !isNavigatingRef.current && !hasInitializedHistoryRef.current) {
      window.history.pushState(null, document.title, window.location.href);
      hasInitializedHistoryRef.current = true;
    }

    if (!shouldIntercept && hasInitializedHistoryRef.current) {
      hasInitializedHistoryRef.current = false;
    }
  }, [shouldIntercept]);

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    document.addEventListener('click', handleLinkClick, true);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
      document.removeEventListener('click', handleLinkClick, true);
      isNavigatingRef.current = false;
      isProcessingRef.current = false;
    };
  }, [handleBeforeUnload, handlePopState, handleLinkClick]);

  const allowNavigation = useCallback(() => {
    isNavigatingRef.current = true;
  }, []);

  return { allowNavigation };
};
